import React from 'react';
import uniqBy from 'lodash/uniqBy';

import _ from '@ihme/common/locale';
import { ChartConsumer } from '@ihme/common/web/components/ChartProvider';
import { LineChart } from '@ihme/common/web/components';

import echartsTheme from '../../theme/echarts';
import { pngIcon } from '../../theme/icons';

import { renderChartCopyright } from "../../utility/chart-copyright";
import formatChartValue from '../../utility/formatting/formatChartValue';
import formatChartLabel from '../../utility/formatting/formatChartLabel';

type Props = {};

class TrendsChart extends React.Component<Props> {
    renderTitle = ({ filters, yearsRange }) =>
        _('trends_chart_title', {
            cause: _(`cause_${filters.cause_id}`),
            measure: _(`measure_${filters.measure_id}`),
            metric: _(`metric_${filters.metric_id}`),
            yearsRange: yearsRange.range
        });

    renderSubtitle = ({ filters, yearsRange }) =>
        _('trends_chart_subtitle', {
            location: _(`location_${filters.location_id}`),
            ageGroup: _(`age_group_${filters.age_group_id}`)
        });

    renderTooltip = (params, { isMobileView }) => {
        const { columns } = this.props;
        const valueIdx = columns.indexOf('value');
        const lowerIdx = columns.indexOf('lower');
        const upperIdx = columns.indexOf('upper');
        const measureIdx = columns.indexOf('measure_id');
        const header = params[0].axisValue;

        const sortedLines = uniqBy(
            params.filter(lineData => lineData.data[valueIdx] !== undefined && lineData.data[valueIdx] !== null),
            lineData => lineData.data[valueIdx]
        ).sort((a, b) => b.value - a.value);

        const parseLineValue = lineParams => {
            const { data } = lineParams;

            const getMarker = color =>
                `<span
                    style='
                        display:inline-block;
                        margin-right:8px;
                        border-radius:10px;
                        width:10px;
                        height:10px;
                        background-color:${color};'>
                </span>`;

            const color =
                typeof lineParams.color === 'object'
                    ? lineParams.color.colorStops[0].color
                    : lineParams.color;

            const upper = formatChartValue(data[upperIdx]);
            const lower = formatChartValue(data[lowerIdx]);

            const uncertainty = upper !== null && lower !== null ? `(${lower} - ${upper})` : '';
            const value = `${formatChartValue(data[valueIdx])} ${uncertainty}`;

            if (isMobileView){
                return `<div class='chart-tooltip-text'>
                    ${getMarker(color)}
                    ${' ' + _(`tooltip_measure_${data[measureIdx]}`) + ': '}
                    <div class='chart-tooltip-text-bold' style='font-size: 13px'>
                        ${value}
                    </div>
                </div>`;
            }

            return `<div class='chart-tooltip-text'>
                    ${getMarker(color)}
                    <span class='chart-tooltip-text-bold'>
                        ${value}
                    </span>
                    ${' ' + _(`tooltip_measure_${data[measureIdx]}`)}
                </div>`;
        };

        return `<div class='chart-tooltip-title'>${header}</div>
               ${sortedLines.map(parseLineValue).join('')}`;
    };

    getSaveFilename = ({
        filters: { cause_id, measure_id, metric_id, age_group_id, location_id },
        yearsRange: { range }
    }) =>
        'Trends Chart' +
        this.getLocalizedFilter(cause_id, 'cause_', ', ') +
        this.getLocalizedFilter(measure_id, 'measure_', ', ') +
        this.getLocalizedFilter(metric_id, 'long_metric_', ', ') +
        this.getLocalizedFilter(location_id, 'location_', ' in ') +
        this.getLocalizedFilter(age_group_id, 'age_group_', ', ') +
        ', ' +
        range;

    getLocalizedFilter = (
        value: string | number,
        localePrefix: string,
        prefix: string = ''
    ): string =>
        value
            ? prefix +
              value
                  .toString()
                  .split(',')
                  .map(id => _(`${localePrefix}${id}`))
                  .join(', ')
            : '';

    render() {
        return (
            <LineChart
                theme={echartsTheme}
                withSlider
                yAxisLabelFormatter={formatChartLabel}
                renderYAxisTitle={({ filters: { metric_id, measure_id } }) =>
                    _(`measure_${measure_id}_full_name`) + ', ' + _(`metric_${metric_id}`)
                }
                renderTooltip={this.renderTooltip}
                renderTitle={this.renderTitle}
                renderSubtitle={this.renderSubtitle}
                renderGraphic={renderProps => renderChartCopyright(renderProps, 76, 50)}
                configureGrid={({ isMobileView }) => ({
                    bottom: isMobileView ? 230 : 120,
                })}
                saveAsImage={{
                    visible: true,
                    filename: this.getSaveFilename,
                    icon: pngIcon,
                }}
            />
        );
    }
}

export default (props) => (
    <ChartConsumer>{(consumerProps) => <TrendsChart {...consumerProps} {...props} />}</ChartConsumer>
);
